import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, TrendingUp, TrendingDown, Sparkles, Users, Trophy, AlertTriangle,
  Plus, Trash2, Wand2, Phone, Mail, MessageSquare, Calendar, HandHelping, DollarSign, UserPlus, Gauge,
  Target, CalendarClock, X, Bell,
} from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip } from "recharts";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";

const TONE = {
  emerald: "bg-emerald-500", green: "bg-green-500", slate: "bg-slate-400", amber: "bg-amber-500", red: "bg-red-500",
};
const CHIP = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700", green: "border-green-200 bg-green-50 text-green-700",
  slate: "border-slate-200 bg-slate-50 text-slate-600", amber: "border-amber-200 bg-amber-50 text-amber-700",
  red: "border-red-200 bg-red-50 text-red-700",
};
const CHANNELS = [
  { key: "email", label: "Email", icon: Mail }, { key: "call", label: "Call", icon: Phone },
  { key: "message", label: "Message / chat", icon: MessageSquare }, { key: "meeting", label: "Meeting", icon: Calendar },
  { key: "other", label: "Other", icon: HandHelping },
];

const ScoreBar = ({ score, tone }) => (
  <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted" data-testid="impact-score-bar">
    <div className={`h-full rounded-full transition-all ${TONE[tone] || "bg-slate-400"}`} style={{ width: `${score}%` }} />
  </div>
);

function GoalCard({ companyId, goal, avg, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState(goal?.target || 65);
  const [period, setPeriod] = useState(goal?.period || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/companies/${companyId}/impact/goal`, { target: Number(target), period }); setEditing(false); onSaved(); toast.success("Goal saved"); }
    catch { toast.error("Could not save goal"); }
    finally { setSaving(false); }
  };
  const clear = async () => {
    try { await api.put(`/companies/${companyId}/impact/goal`, { target: null }); setEditing(false); onSaved(); }
    catch { toast.error("Could not clear goal"); }
  };

  if (!goal && !editing) {
    return (
      <Card className="flex items-center justify-between rounded-xl border border-dashed p-4" data-testid="impact-goal-empty">
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Target className="h-4 w-4 text-primary" /> Set a target average impact score to track progress this quarter.</div>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="impact-goal-set">Set a goal</Button>
      </Card>
    );
  }
  if (editing) {
    return (
      <Card className="rounded-xl border p-4" data-testid="impact-goal-edit">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Target average</Label><Input type="number" min={1} max={100} value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1 w-24" data-testid="impact-goal-target" /></div>
          <div><Label className="text-xs">Period (optional)</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="e.g. Q3 2026" className="mt-1 w-40" data-testid="impact-goal-period" /></div>
          <Button size="sm" onClick={save} disabled={saving} className="gap-1.5" data-testid="impact-goal-save">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save</Button>
          <Button size="sm" variant="ghost" onClick={() => (goal ? setEditing(false) : clear())}>Cancel</Button>
          {goal && <Button size="sm" variant="ghost" className="text-red-500" onClick={clear} data-testid="impact-goal-clear">Remove</Button>}
        </div>
      </Card>
    );
  }
  const g = goal;
  return (
    <Card className="rounded-xl border p-4" data-testid="impact-goal-card">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Target className="h-4 w-4 text-primary" /> Impact goal{g.period ? ` · ${g.period}` : ""}</div>
        <button onClick={() => { setTarget(g.target); setPeriod(g.period || ""); setEditing(true); }} className="text-xs text-primary hover:underline" data-testid="impact-goal-editbtn">Edit</button>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Avg {g.current} / target {g.target}</span>
        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
          <div className={`h-full rounded-full ${g.met ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${g.pct}%` }} data-testid="impact-goal-bar" />
        </div>
        <Badge variant="outline" className={g.met ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-600"}>{g.met ? "Met 🎉" : `${g.pct}%`}</Badge>
      </div>
    </Card>
  );
}

export default function InvestorImpact() {
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [logOpen, setLogOpen] = useState(false);

  const load = async () => {
    if (!company) return;
    try { const res = await api.get(`/companies/${company.id}/impact`); setData(res.data); }
    catch { setData({ investors: [], summary: {}, company_stage: "" }); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const openDetail = async (investorId) => {
    try { const res = await api.get(`/companies/${company.id}/impact/${investorId}`); setDetail(res.data); }
    catch { toast.error("Could not load investor"); }
  };

  return (
    <AppShell title="Investor Impact" subtitle={`How helpful or disruptive each investor is - weighted for your ${data?.company_stage || ""} stage`}>
      {!data ? (
        <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-6" data-testid="investor-impact-page">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card className="rounded-xl border p-4"><div className="flex items-center gap-2 text-2xl font-semibold"><Gauge className="h-5 w-5 text-primary" />{data.summary.avg_score ?? 0}</div><div className="text-xs text-muted-foreground">Avg impact score</div></Card>
            <Card className="rounded-xl border p-4"><div className="flex items-center gap-2 text-2xl font-semibold"><Users className="h-5 w-5 text-slate-400" />{data.summary.count ?? 0}</div><div className="text-xs text-muted-foreground">Investors</div></Card>
            <Card className="rounded-xl border bg-emerald-50/40 p-4"><div className="flex items-center gap-2 text-2xl font-semibold text-emerald-700"><Trophy className="h-5 w-5" />{data.summary.champions ?? 0}</div><div className="text-xs text-muted-foreground">Helpful / champions</div></Card>
            <Card className="rounded-xl border bg-amber-50/40 p-4"><div className="flex items-center gap-2 text-2xl font-semibold text-amber-700"><AlertTriangle className="h-5 w-5" />{data.summary.at_risk ?? 0}</div><div className="text-xs text-muted-foreground">Friction / disruptive</div></Card>
          </div>

          <GoalCard companyId={company.id} goal={data.goal} avg={data.summary.avg_score ?? 0} onSaved={load} />

          {data.investors.length === 0 ? (
            <Card className="rounded-xl border p-12 text-center text-muted-foreground">Add investors to start scoring their impact.</Card>
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {data.investors.map((p) => (
                <Card key={p.investor_id} className="cursor-pointer rounded-xl border p-4 transition-colors hover:border-primary/40" onClick={() => openDetail(p.investor_id)} data-testid={`impact-card-${p.investor_id}`}>
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-foreground">{p.investor_name}</div>
                      <div className="truncate text-[11px] text-muted-foreground">{p.investor_email}</div>
                    </div>
                    <Badge variant="outline" className={CHIP[p.band.tone]} data-testid={`impact-band-${p.investor_id}`}>{p.band.label}</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-9 text-right text-lg font-bold text-foreground" data-testid={`impact-score-${p.investor_id}`}>{p.score}</span>
                    <ScoreBar score={p.score} tone={p.band.tone} />
                    {typeof p.vs_avg === "number" && (
                      <span className={`shrink-0 text-[11px] font-semibold ${p.vs_avg >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid={`impact-vsavg-${p.investor_id}`}>
                        {p.vs_avg >= 0 ? "+" : ""}{p.vs_avg} vs avg
                      </span>
                    )}
                  </div>
                  {p.projected_effects.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p.projected_effects.slice(0, 3).map((e, i) => (
                        <span key={i} className="rounded-md bg-secondary px-2 py-0.5 text-[11px] text-foreground">{e.text}</span>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {detail && (
        <DetailDialog detail={detail} companyId={company.id} onClose={() => setDetail(null)}
          onLog={() => setLogOpen(true)}
          onChange={(fresh) => { setDetail((d) => ({ ...fresh, actions_catalog: d.actions_catalog })); load(); }} />
      )}
      {detail && logOpen && (
        <LogDialog companyId={company.id} investor={detail} onClose={() => setLogOpen(false)}
          onLogged={(fresh) => { setLogOpen(false); setDetail((d) => ({ ...fresh, actions_catalog: d.actions_catalog })); load(); }} />
      )}
    </AppShell>
  );
}

function DetailDialog({ detail, companyId, onClose, onLog, onChange }) {
  const [askingKey, setAskingKey] = useState(null);
  const removeEvent = async (id) => {
    try { await api.delete(`/impact/events/${id}`); const res = await api.get(`/companies/${companyId}/impact/${detail.investor_id}`); onChange(res.data); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };
  const createAsk = async (s) => {
    setAskingKey(s.action_key);
    try { await api.post(`/companies/${companyId}/impact/${detail.investor_id}/ask`, { action_key: s.action_key }); toast.success(`Ask created: ${s.label}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not create ask"); }
    finally { setAskingKey(null); }
  };
  const [nudgeDate, setNudgeDate] = useState("");
  const [nudgeNote, setNudgeNote] = useState("");
  const [nudging, setNudging] = useState(false);
  const refresh = async () => { const res = await api.get(`/companies/${companyId}/impact/${detail.investor_id}`); onChange(res.data); };
  const scheduleNudge = async () => {
    if (!nudgeDate) return toast.error("Pick a date");
    setNudging(true);
    try { await api.post(`/companies/${companyId}/impact/${detail.investor_id}/nudge`, { remind_at: nudgeDate, note: nudgeNote }); setNudgeDate(""); setNudgeNote(""); await refresh(); toast.success("Nudge scheduled"); }
    catch { toast.error("Could not schedule"); }
    finally { setNudging(false); }
  };
  const cancelNudge = async (id) => {
    try { await api.delete(`/impact/nudges/${id}`); await refresh(); }
    catch { toast.error("Could not cancel"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="impact-detail-dialog">
        <DialogHeader>
          <DialogTitle>{detail.investor_name}</DialogTitle>
          <DialogDescription>{detail.investor_email} · weighted for {detail.stage} stage</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border p-4">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-4xl font-bold text-foreground" data-testid="impact-detail-score">{detail.score}</span>
              <Badge variant="outline" className={CHIP[detail.band.tone]}>{detail.band.label}</Badge>
            </div>
            <ScoreBar score={detail.score} tone={detail.band.tone} />
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="flex items-center gap-1 text-emerald-700"><TrendingUp className="h-3.5 w-3.5" /> +{detail.helpful_points} helpful</span>
              <span className="flex items-center gap-1 text-red-600"><TrendingDown className="h-3.5 w-3.5" /> {detail.disruptive_points} disruptive</span>
              {typeof detail.vs_avg === "number" && (
                <span className={`ml-auto font-medium ${detail.vs_avg >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="impact-detail-vsavg">
                  {detail.vs_avg >= 0 ? "+" : ""}{detail.vs_avg} vs portfolio avg ({detail.company_avg})
                </span>
              )}
            </div>
          </div>

          {detail.timeline && detail.timeline.length > 1 && (
            <div className="rounded-xl border p-3" data-testid="impact-timeline">
              <div className="mb-1 text-xs font-semibold text-foreground">Score trend</div>
              <div className="h-28 w-full">
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 400, height: 112 }}>
                  <LineChart data={detail.timeline} margin={{ top: 6, right: 8, left: -20, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} tickLine={false} axisLine={false} width={28} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                      <Line type="monotone" dataKey="score" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {detail.projected_effects.length > 0 && (
            <div>
              <div className="mb-1.5 text-xs font-semibold text-foreground">Projected effect (estimated)</div>
              <div className="flex flex-wrap gap-1.5">
                {detail.projected_effects.map((e, i) => (
                  <span key={i} className="rounded-md border bg-secondary/50 px-2 py-1 text-[11px] text-foreground" data-testid={`impact-effect-${i}`}>{e.text}</span>
                ))}
              </div>
            </div>
          )}

          {detail.suggested_asks && detail.suggested_asks.length > 0 && (
            <div data-testid="impact-suggested-asks">
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-foreground"><HandHelping className="h-3.5 w-3.5 text-primary" /> Turn into a tracked ask</div>
              <div className="space-y-1.5">
                {detail.suggested_asks.map((s) => (
                  <div key={s.action_key} className="flex items-center justify-between gap-2 rounded-lg border p-2" data-testid={`impact-ask-${s.action_key}`}>
                    <span className="min-w-0 truncate text-xs text-foreground">{s.label} <span className="text-emerald-700">+{s.points}</span></span>
                    <Button size="sm" variant="outline" className="h-7 shrink-0 gap-1 text-[11px]" disabled={askingKey === s.action_key} onClick={() => createAsk(s)} data-testid={`impact-ask-btn-${s.action_key}`}>
                      {askingKey === s.action_key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Create ask
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detail.auto_signals.length > 0 && (
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-foreground"><Sparkles className="h-3.5 w-3.5 text-primary" /> Automatic signals</div>
              <div className="space-y-1">
                {detail.auto_signals.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">{s.label}</span>
                    <span className={s.points >= 0 ? "text-emerald-700" : "text-red-600"}>{s.points >= 0 ? "+" : ""}{s.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div data-testid="impact-nudges">
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-foreground"><CalendarClock className="h-3.5 w-3.5 text-primary" /> Schedule a follow-up nudge</div>
            {(detail.nudges || []).length > 0 && (
              <div className="mb-2 space-y-1">
                {detail.nudges.map((n) => (
                  <div key={n.id} className="flex items-center justify-between gap-2 rounded-lg border bg-secondary/30 p-2 text-[11px]" data-testid={`nudge-${n.id}`}>
                    <span className="flex items-center gap-1.5 text-foreground"><Bell className="h-3 w-3 text-primary" /> {n.remind_at}{n.note ? ` · ${n.note}` : ""}</span>
                    <button onClick={() => cancelNudge(n.id)} className="text-muted-foreground hover:text-red-600" data-testid={`nudge-cancel-${n.id}`}><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <DatePicker value={nudgeDate} onChange={setNudgeDate} className="h-8 w-44 text-xs" data-testid="nudge-date" />
              <Input value={nudgeNote} onChange={(e) => setNudgeNote(e.target.value)} placeholder="Note (optional)" className="h-8 flex-1 text-xs" data-testid="nudge-note" />
              <Button size="sm" className="h-8 shrink-0 gap-1 text-[11px]" onClick={scheduleNudge} disabled={nudging} data-testid="nudge-schedule">
                {nudging ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Schedule
              </Button>
            </div>
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground">Logged interactions ({detail.events.length})</span>
              <Button size="sm" className="h-7 gap-1.5 text-[11px]" onClick={onLog} data-testid="impact-log-open"><Plus className="h-3.5 w-3.5" /> Log interaction</Button>
            </div>
            {detail.events.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">No manual interactions logged yet. Log calls, emails or offline actions to sharpen the score.</p>
            ) : (
              <div className="max-h-48 space-y-1.5 overflow-y-auto">
                {detail.events.map((e) => (
                  <div key={e.id} className="flex items-start justify-between gap-2 rounded-lg border p-2" data-testid={`impact-event-${e.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-xs font-medium text-foreground">{e.label}</span>
                        <span className={`shrink-0 text-[11px] ${e.points >= 0 ? "text-emerald-700" : "text-red-600"}`}>{e.points >= 0 ? "+" : ""}{e.points}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground">{e.channel} · {formatDate(e.occurred_at || e.created_at)}{e.note ? ` · ${e.note}` : ""}</div>
                    </div>
                    <button onClick={() => removeEvent(e.id)} className="shrink-0 text-muted-foreground hover:text-red-600" data-testid={`impact-event-del-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LogDialog({ companyId, investor, onClose, onLogged }) {
  const catalog = investor.actions_catalog || [];
  const helpful = catalog.filter((a) => a.category === "helpful");
  const disruptive = catalog.filter((a) => a.category === "disruptive");
  const [actionKey, setActionKey] = useState("");
  const [channel, setChannel] = useState("email");
  const [note, setNote] = useState("");
  const [paste, setPaste] = useState("");
  const [classifying, setClassifying] = useState(false);
  const [aiHint, setAiHint] = useState(null);
  const [saving, setSaving] = useState(false);

  const classify = async () => {
    if (!paste.trim()) return toast.error("Paste an email or message first");
    setClassifying(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/impact/classify`, { text: paste });
      setAiHint(data);
      if (data.action_key && data.action_key !== "custom") setActionKey(data.action_key);
      if (data.channel) setChannel(data.channel);
      if (!note) setNote(paste.slice(0, 200));
      toast.success("Classified - review and log");
    } catch { toast.error("Could not classify"); }
    finally { setClassifying(false); }
  };

  const submit = async () => {
    if (!actionKey) return toast.error("Pick an action");
    setSaving(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/impact/${investor.investor_id}/events`, { action_key: actionKey, channel, note });
      toast.success("Logged");
      onLogged(data);
    } catch { toast.error("Could not log"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="impact-log-dialog">
        <DialogHeader>
          <DialogTitle>Log an interaction</DialogTitle>
          <DialogDescription>Record a call, email, message or offline action by {investor.investor_name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border bg-secondary/40 p-3">
            <Label className="flex items-center gap-1.5 text-xs"><Wand2 className="h-3.5 w-3.5 text-primary" /> Paste an email or message (AI will classify it)</Label>
            <Textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={3} placeholder="Paste the investor's email or chat message here…" className="mt-1.5 text-xs" data-testid="impact-paste" />
            <Button size="sm" variant="outline" className="mt-2 gap-1.5" onClick={classify} disabled={classifying} data-testid="impact-classify-btn">
              {classifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Classify with AI
            </Button>
            {aiHint && <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="impact-ai-hint">{aiHint.rationale}</p>}
          </div>

          <div>
            <Label>Action</Label>
            <Select value={actionKey} onValueChange={setActionKey}>
              <SelectTrigger className="mt-1" data-testid="impact-action-select"><SelectValue placeholder="Choose what happened" /></SelectTrigger>
              <SelectContent>
                {helpful.map((a) => <SelectItem key={a.key} value={a.key}>👍 {a.label}</SelectItem>)}
                {disruptive.map((a) => <SelectItem key={a.key} value={a.key}>⚠️ {a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="mt-1" data-testid="impact-channel-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHANNELS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened" className="mt-1" data-testid="impact-note" /></div>

          <Button onClick={submit} disabled={saving} className="w-full gap-2" data-testid="impact-log-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Log interaction
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
